import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { ExpandMore, Save, Cancel } from "@mui/icons-material";
import config from "../../config";

const baseUrl = config.baseURL;

// Default actions to show for all modules
const defaultActions = ["add", "edit", "show", "delete"];

const EditUserPage = ({ editorRole }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    role: "user",
    password: "",
    access: {},
  });
  const [oldPassword, setOldPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState({
    customer: { add: false, edit: false, show: false, delete: false },
    categories: { add: false, edit: false, show: false, delete: false },
    complaint: { add: false, show: false },
    earnings: { add: false, edit: false, show: false, delete: false },
    expenses: { add: false, edit: false, show: false, delete: false },
    faqs: { show: false },
    invoice: { show: false },
    materials: { add: false, edit: false, show: false, delete: false },
    orders: { add: false, edit: false, show: false, delete: false },
    pages: { add: false, edit: false, show: false, delete: false },
    ProductionBatch: { add: false, edit: false, show: false, delete: false },
    products: { add: false, edit: false, show: false, delete: false },
    rewardPoints: { add: false, edit: false, show: false, delete: false },
    ShippingCompany: { add: false, edit: false, show: false, delete: false },
    suppliers: { add: false, edit: false, show: false, delete: false },
    rewardsettings: { add: false, edit: false, show: false, delete: false },
    pricing: {},
    careers: {},
    zones: {},
    marketing: { add: false, edit: false, show: false, delete: false },
    ads: { add: false, edit: false, show: false, delete: false },
    advertiser: { add: false, edit: false, show: false, delete: false },
  });
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${baseUrl}/api/newUser/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const user = response.data.data;
        const parsedAccess =
          typeof user.access === "string"
            ? JSON.parse(user.access)
            : user.access;

        // Ensure all modules have all default actions
        const fullAccess = { ...parsedAccess };
        Object.keys(fullAccess).forEach((module) => {
          defaultActions.forEach((action) => {
            if (!(action in fullAccess[module])) {
              fullAccess[module][action] = false;
            }
          });
        });

        setUserData({
          firstname: user.firstname || "",
          lastname: user.lastname || "",
          email: user.email || "",
          role: user.role || "user",
          password: "",
          access: fullAccess,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user:", error);
        setLoading(false);
      }
    };
    fetchUserData();
  }, [id]);

  const handleChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccessChange = (module, action, e) => {
    setUserData((prev) => ({
      ...prev,
      access: {
        ...prev.access,
        [module]: { ...prev.access[module], [action]: e.target.checked },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (userData.password && userData.password !== confirmPassword) {
      alert("Password and Confirm Password do not match!");
      return;
    }

    try {
      const payload = {
        ...userData,
        access: JSON.stringify(userData.access),
      };

      await axios.put(`${baseUrl}/api/newUser/${id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      alert("User updated successfully!");
      navigate("/dashboard/UsersPage");
    } catch (error) {
      console.error("Error updating user:", error.response?.data || error);
      alert("Error updating user. Check console for details.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 5 } }}>
      <Typography variant="h4" gutterBottom>
        Edit User: {userData.firstname} {userData.lastname}
      </Typography>
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={userData.firstname}
                onChange={(e) => handleChange("firstname", e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={userData.lastname}
                onChange={(e) => handleChange("lastname", e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                value={userData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid container spacing={2} direction="column">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New Password (leave blank to keep current)"
                  type="password"
                  value={userData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  fullWidth
                />
              </Grid>

              {userData.password && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Box sx={{ mt: 5 }}>
            <Typography variant="h6">Access & Permissions</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {Object.keys(access).map((module) => (
                <Grid item xs={12} sm={6} md={4} key={module}>
                  <Accordion
                    sx={{
                      "& .MuiAccordionSummary-root": {
                        backgroundColor: "#F4F6F9",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls={`${module}-content`}
                      id={`${module}-header`}
                    >
                      <Typography sx={{ fontWeight: "bold" }}>
                        {module.charAt(0).toUpperCase() + module.slice(1)}{" "}
                        Module
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <FormGroup>
                        {defaultActions.map((action) => (
                          <FormControlLabel
                            key={action}
                            control={
                              <Checkbox
                                checked={
                                  userData.access[module]?.[action] || false
                                }
                                onChange={(e) =>
                                  handleAccessChange(module, action, e)
                                }
                              />
                            }
                            label={`Can ${action} ${
                              module.charAt(0).toUpperCase() + module.slice(1)
                            }`}
                          />
                        ))}
                      </FormGroup>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/dashboard/UsersPage")}
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" startIcon={<Save />}>
              Update User
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default EditUserPage;
